import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { User, Building2, ArrowRight } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Captcha } from "@/components/Captcha";
import api, { apiError } from "@/lib/api";
import { useAuth, dashboardPath } from "@/context/AuthContext";

export default function RegisterClient() {
  const [accountType, setAccountType] = useState("particulier");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "" });
  const [captcha, setCaptcha] = useState({ token: "", answer: "", website: "" });
  const [busy, setBusy] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const isPro = accountType === "entreprise";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha.answer) return toast.error("Merci de répondre à la question anti-spam");
    if (isPro && !form.company) return toast.error("Indiquez le nom de votre entreprise");
    setBusy(true);
    try {
      const payload = { ...form, role: "client", account_type: accountType, captcha_token: captcha.token, captcha_answer: captcha.answer, website: captcha.website };
      const { data } = await api.post("/auth/register", payload);
      loginWithToken(data.token, data.user);
      toast.success("Compte créé avec succès !");
      navigate(dashboardPath(data.user.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/espace-client";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-lg px-4 py-16">
        <h1 className="text-3xl font-extrabold text-[#263871]">Créer mon compte client</h1>
        <p className="mt-2 text-slate-600">Réservez l'entretien de votre clim et suivez vos interventions.</p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button type="button" data-testid="account-particulier" onClick={() => setAccountType("particulier")} className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-4 text-center transition-colors ${!isPro ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200 bg-white"}`}>
            <User className={`h-6 w-6 ${!isPro ? "text-[#1D9BF0]" : "text-slate-400"}`} />
            <span className="text-sm font-bold text-[#263871]">Particulier</span>
          </button>
          <button type="button" data-testid="account-entreprise" onClick={() => setAccountType("entreprise")} className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-4 text-center transition-colors ${isPro ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200 bg-white"}`}>
            <Building2 className={`h-6 w-6 ${isPro ? "text-[#1D9BF0]" : "text-slate-400"}`} />
            <span className="text-sm font-bold text-[#263871]">Entreprise</span>
          </button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          {isPro && (
            <div>
              <Label htmlFor="company">Nom de l'entreprise</Label>
              <Input id="company" data-testid="register-company" required value={form.company} onChange={set("company")} className="mt-1 h-12 rounded-xl" />
            </div>
          )}
          <div>
            <Label htmlFor="name">{isPro ? "Nom du contact" : "Nom complet"}</Label>
            <Input id="name" data-testid="register-name" required value={form.name} onChange={set("name")} className="mt-1 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="mt-1 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="phone">Téléphone</Label>
            <Input id="phone" data-testid="register-phone" value={form.phone} onChange={set("phone")} className="mt-1 h-12 rounded-xl" />
          </div>
          <Captcha testid="register-captcha" onChange={(v) => setCaptcha((p) => ({ ...p, ...v }))} />
          <Button data-testid="register-submit" type="submit" disabled={busy} className="h-12 w-full rounded-full bg-[#263871] hover:bg-[#35508F]">
            {busy ? "Création…" : "Créer mon compte"}
          </Button>
        </form>
        <div className="my-5 flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" />OU<span className="h-px flex-1 bg-slate-200" /></div>
        <Button data-testid="register-google" variant="outline" onClick={google} className="h-12 w-full rounded-full border-2">Continuer avec Google</Button>
        <p className="mt-6 text-center text-sm text-slate-600">Déjà inscrit ? <Link to="/connexion" className="font-semibold text-[#1D9BF0]">Connexion</Link></p>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
          <p className="text-sm text-slate-600">Vous êtes un professionnel de la clim ?</p>
          <Link to="/rejoindre" data-testid="goto-pro" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#1D9BF0]">Rejoindre le réseau Climateasy <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </PublicLayout>
  );
}
